import {
	memo,
	FC,
	useState,
	ChangeEvent,
	KeyboardEvent,
	HTMLAttributes,
	useEffect,
	useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { api, clx } from 'Utils';
import { FontAwesome } from 'Elements';
import { ColorValues, GeoAddress } from 'Types';
import { useAxios, useHasMount, useResponsive } from 'Hooks';
import styles from 'partials/shared/postal-code-validator.module.scss';

interface Props extends Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> {
	defaultPostalValue?: GeoAddress[];
	containerClassName?: string;
	inputValue?: string;
	boxClassName?: string;
	inputClassName?: string;
	onChange?: (data: string) => void;
	onFind?: (data: GeoAddress) => void;
	hideMunicipality?: boolean;
}

export const PostalCodeValidator: FC<Props> = memo(
	({
		defaultPostalValue = null,
		inputValue = '',
		containerClassName,
		boxClassName,
		inputClassName,
		onFind,
		onChange,
		hideMunicipality,
		...inputProps
	}) => {
		const controllerRef = useRef<AbortController | null>(null);

		const { t } = useTranslation();
		const hasMount = useHasMount();
		const [postalValue, setPostalValue] = useState(inputValue ?? '');
		const { isMobileAndBelow } = useResponsive();
		const { sendRequest, isLoading } = useAxios<{
			results: GeoAddress[];
		}>();
		const [postalCodeData, setPostalCodeData] = useState<
			GeoAddress[] | null
		>(defaultPostalValue);
		const getPostalCodeData = async (postalCode: string) => {
			const controller = new AbortController();
			controllerRef.current = controller;
			await sendRequest(
				api.searchPostalCity(postalCode),
				{ method: 'GET', signal: controller.signal },
				(data) => {
					if (data) {
						setPostalCodeData(data.results);
						onFind?.(data.results?.[0]);
					}
				}
			);
		};

		const checkInputAndSubmit = async (value: string) => {
			if (value.length === 5) {
				await getPostalCodeData(value);
			} else {
				setPostalCodeData(null);
			}
		};

		const handleInputChange = async (e: ChangeEvent<HTMLInputElement>) => {
			if (controllerRef.current) {
				controllerRef.current.abort();
			}
			const value = e.target.value;
			setPostalValue(value);
			await checkInputAndSubmit(value);
			onChange?.(value);
		};

		const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
			if (e.which < 48 || e.which > 57) {
				e.preventDefault();
			}
		};

		useEffect(() => {
			setPostalValue(inputValue);
		}, [inputValue]);

		useEffect(() => {
			if (!defaultPostalValue) {
				setPostalCodeData(null);
			}
		}, [defaultPostalValue, inputValue]);

		return (
			<div className={clx('d-flex flex-column', containerClassName)}>
				{hasMount && (
					<div
						aria-invalid={
							inputProps['aria-invalid'] ||
							postalCodeData?.length === 0
						}
						aria-disabled={inputProps['aria-disabled']}
						className={clx(
							'position-relative gap-2',
							boxClassName
						)}>
						<FontAwesome icon="location-dot" size="lg" />
						<input
							{...inputProps}
							type="text"
							inputMode="numeric"
							autoComplete="off"
							maxLength={5}
							minLength={5}
							value={postalValue}
							disabled={!!inputProps['aria-disabled']}
							className={clx(styles.inputbox, inputClassName)}
							placeholder={t('placeholder.search-postal-code')}
							onKeyPress={handleKeyPress}
							onInput={handleInputChange}
						/>
						{isLoading && (
							<span className={styles.inputMeta}>
								<FontAwesome
									spin
									spinPulse
									icon="spinner"
									size="xl"
									fixedWidth
								/>
							</span>
						)}
						{!isLoading && postalCodeData && (
							<div className={styles.inputMeta}>
								{!isMobileAndBelow &&
									postalCodeData.length === 0 && (
										<span className="text-red fs-small">
											{t(
												'validation.postal-code.invalid'
											)}
										</span>
									)}
								{!hideMunicipality &&
									postalCodeData.length > 0 && (
										<span
											className={clx(
												styles.meta,
												'text-truncate text-gray-3 fs-small'
											)}>
											{
												postalCodeData[0]?.address
													?.municipality
											}
										</span>
									)}
								<FontAwesome
									icon={
										postalCodeData.length > 0
											? 'check'
											: 'xmark'
									}
									size="lg"
									swapOpacity={postalCodeData.length === 0}
									color={
										postalCodeData.length > 0
											? ColorValues.primary
											: ColorValues.red
									}
								/>
							</div>
						)}
					</div>
				)}
				{isMobileAndBelow &&
					!isLoading &&
					postalCodeData &&
					postalCodeData.length === 0 && (
						<div className="invalid-feedback d-block">
							{t('validation.postal-code.invalid')}
						</div>
					)}
			</div>
		);
	}
);

PostalCodeValidator.displayName = 'PostalCodeValidator';
